import { DEVICE_KEYS, normalizeDevices } from './devices';
import { normalizeResponsive } from './templates/responsive';
import {
	createDefaultSettings,
	defaultDisplayRules,
	DEFAULT_GOOGLE_FONT_WEIGHTS,
	DEFAULT_TEMPLATE,
	GOOGLE_FONT_WEIGHTS,
	GOOGLE_FONTS,
} from './defaults';
import { createBaseTemplate } from './templates';
import { COMPONENTS } from './templates/components';
import {
	BannerContent,
	ComponentKey,
	ComponentSettings,
	CountdownSettings,
	DisplayRules,
	MediaKey,
	MediaSettings,
	SiteTimezone,
	StickyBannerSettingsPayload,
	TemplateDesign,
	TextKey,
	TypographySettings,
} from './types';

type UnknownRecord = Record< string, unknown >;

const isRecord = ( value: unknown ): value is UnknownRecord =>
	!! value && typeof value === 'object' && ! Array.isArray( value );

const toStr = ( value: unknown, fallback: string ): string => {
	if ( typeof value === 'string' ) {
		return value;
	}
	if ( typeof value === 'number' && Number.isFinite( value ) ) {
		return String( value );
	}
	return fallback;
};

const toBool = ( value: unknown, fallback: boolean ): boolean => {
	if ( typeof value === 'boolean' ) {
		return value;
	}
	if ( value === 'true' || value === '1' || value === 1 ) {
		return true;
	}
	if ( value === 'false' || value === '0' || value === 0 || value === '' ) {
		return false;
	}
	return fallback;
};

/**
 * Older versions saved the bundled default images as absolute URLs
 * (e.g. .../build/images/banner-bg.b80f3c45.png or .../src/components/...).
 * Those break on rebuilds/migrations, so treat them as "use default".
 */
const isLegacyDefaultAssetUrl = ( url: string ): boolean =>
	/\/build\/images\/|\/src\/components\/stickyBanner\/templates\//.test(
		url
	);

/**
 * Merge saved text settings with a template's defaults. Unknown keys are dropped.
 *
 * @param raw      Saved typography map.
 * @param defaults Template default typography.
 */
const normalizeTypography = (
	raw: unknown,
	defaults: Record< string, TypographySettings >
): Record< string, TypographySettings > => {
	const rawTypography = isRecord( raw ) ? raw : {};
	const typography: Record< string, TypographySettings > = {};
	Object.keys( defaults ).forEach( ( key ) => {
		const base = defaults[ key ];
		const item = isRecord( rawTypography[ key ] )
			? ( rawTypography[ key ] as UnknownRecord )
			: {};
		const align = toStr( item.align, base.align );

		const next: TypographySettings = {
			...base,
			text: toStr( item.text, base.text ),
			fontFamily: toStr( item.fontFamily, base.fontFamily ),
			weight: toStr( item.weight, base.weight ),
			size: toStr( item.size, base.size ),
			lineHeight: toStr( item.lineHeight, base.lineHeight ),
			color: toStr( item.color, base.color ),
			align:
				align === 'left' || align === 'center' || align === 'right'
					? align
					: base.align,
			isVisible: toBool( item.isVisible, base.isVisible ),
		};

		// Gradient text (kept when switched back to solid, so it can return).
		if ( item.colorType === 'gradient' || item.colorType === 'solid' ) {
			next.colorType = item.colorType;
		}
		( [ 'gradientFrom', 'gradientTo', 'gradientAngle' ] as const ).forEach(
			( field ) => {
				if ( typeof item[ field ] === 'string' ) {
					next[ field ] = item[ field ] as string;
				}
			}
		);

		if ( base.spacing ) {
			const spacing = isRecord( item.spacing ) ? item.spacing : {};
			next.spacing = {
				top: toStr( spacing.top, base.spacing.top ),
				right: toStr( spacing.right, base.spacing.right ),
				bottom: toStr( spacing.bottom, base.spacing.bottom ),
				left: toStr( spacing.left, base.spacing.left ),
			};
		}

		if ( base.link !== undefined ) {
			next.link = toStr( item.link, base.link );
			next.openInNewTab = toBool(
				item.openInNewTab,
				!! base.openInNewTab
			);
		}

		typography[ key ] = next;
	} );

	return typography;
};

/**
 * Merge saved image settings with a template's defaults.
 *
 * @param raw      Saved media map.
 * @param defaults Template default media.
 */
const normalizeMedia = (
	raw: unknown,
	defaults: Record< string, MediaSettings >
): Record< string, MediaSettings > => {
	const rawMedia = isRecord( raw ) ? raw : {};
	const media: Record< string, MediaSettings > = {};
	Object.keys( defaults ).forEach( ( key ) => {
		const base = defaults[ key ];
		const item = isRecord( rawMedia[ key ] )
			? ( rawMedia[ key ] as UnknownRecord )
			: {};
		const url = toStr( item.url, base.url ).trim();

		const next: MediaSettings = {
			url: isLegacyDefaultAssetUrl( url ) ? '' : url,
			width: toStr( item.width, base.width ),
			height: toStr( item.height, base.height ),
			borderRadius: toStr( item.borderRadius, base.borderRadius ),
			isVisible: toBool( item.isVisible, base.isVisible ),
		};
		media[ key ] = next;
	} );

	return media;
};

const TEXT_KEYS = Object.keys( createBaseTemplate().typography ) as TextKey[];

/**
 * @param raw      Saved banner content.
 * @param defaults Default content.
 */
const normalizeContent = (
	raw: UnknownRecord,
	defaults: BannerContent
): BannerContent => {
	const texts = isRecord( raw.texts ) ? raw.texts : {};
	return {
		texts: TEXT_KEYS.reduce(
			( all, key ) => ( {
				...all,
				[ key ]: toStr( texts[ key ], defaults.texts[ key ] ),
			} ),
			{} as Record< TextKey, string >
		),
		ctaLink: toStr( raw.ctaLink, '' ).trim(),
		ctaNewTab: toBool( raw.ctaNewTab, false ),
	};
};

/**
 * Content of a banner saved before the template library.
 *
 * @param rawTypography Legacy typography map.
 * @param defaults      Default content.
 */
const legacyContentFromTypography = (
	rawTypography: unknown,
	defaults: BannerContent
): BannerContent => {
	const typography = isRecord( rawTypography ) ? rawTypography : {};
	const textOf = ( key: string ): unknown =>
		isRecord( typography[ key ] )
			? ( typography[ key ] as UnknownRecord ).text
			: undefined;
	const cta = isRecord( typography.cta ) ? typography.cta : {};
	return {
		texts: TEXT_KEYS.reduce(
			( all, key ) => ( {
				...all,
				[ key ]: toStr( textOf( key ), defaults.texts[ key ] ),
			} ),
			{} as Record< TextKey, string >
		),
		ctaLink: toStr( cta.link, '' ).trim(),
		ctaNewTab: toBool( cta.openInNewTab, false ),
	};
};

/**
 * Merge a saved template with the base template and coerce every field,
 * so the designer and the banner never see missing values.
 *
 * @param raw Template as returned by the REST API or localized by PHP.
 */
export const normalizeTemplate = ( raw: unknown ): TemplateDesign => {
	const base = createBaseTemplate();
	const source = isRecord( raw ) ? raw : {};
	const rawComponents = isRecord( source.components )
		? source.components
		: {};

	const components = COMPONENTS.reduce(
		( all, definition ) => {
			const defaults = base.components[ definition.key ];
			// Templates saved before "Fourth Text" existed: it was part of
			// the headline, so it starts shown/hidden like the headline.
			const rawItem =
				rawComponents[ definition.key ] ??
				( definition.key === 'fourthText' &&
				isRecord( rawComponents.headline )
					? { visible: rawComponents.headline.visible }
					: undefined );
			const item = isRecord( rawItem )
				? ( rawItem as UnknownRecord )
				: {};
			const variant = toStr( item.variant, defaults.variant );
			const rawStyle = isRecord( item.style ) ? item.style : {};
			const style = { ...defaults.style };
			Object.keys( defaults.style ).forEach( ( key ) => {
				style[ key ] = toStr( rawStyle[ key ], defaults.style[ key ] );
			} );
			const next: ComponentSettings = {
				visible: definition.canHide
					? toBool( item.visible, defaults.visible )
					: true,
				variant: definition.variants.some(
					( option ) => option.id === variant
				)
					? variant
					: defaults.variant,
				style,
			};
			return { ...all, [ definition.key ]: next };
		},
		{} as Record< ComponentKey, ComponentSettings >
	);

	return {
		id: toStr( source.id, base.id ).trim() || base.id,
		name: toStr( source.name, base.name ).trim() || base.name,
		components,
		typography: normalizeTypography(
			source.typography,
			base.typography
		) as Record< TextKey, TypographySettings >,
		media: normalizeMedia( source.media, base.media ) as Record<
			MediaKey,
			MediaSettings
		>,
		responsive: normalizeResponsive( source.responsive ),
		updatedAt: toStr( source.updatedAt, '' ) || undefined,
	};
};

/**
 * Template library with the base template always first.
 *
 * @param raw Templates from the REST API.
 */
export const normalizeTemplateList = ( raw: unknown ): TemplateDesign[] => {
	const list = Array.isArray( raw ) ? raw.map( normalizeTemplate ) : [];
	const base =
		list.find( ( item ) => item.id === DEFAULT_TEMPLATE ) ||
		createBaseTemplate();
	return [ base, ...list.filter( ( item ) => item.id !== base.id ) ];
};

/**
 * Merge raw (saved / API) settings with defaults and coerce every field
 * to the expected type so the UI and template never see undefined values.
 *
 * @param raw Settings as returned by the REST API or localized by PHP.
 */
export const normalizeSettings = (
	raw: unknown
): StickyBannerSettingsPayload => {
	const defaults = createDefaultSettings();
	const source = isRecord( raw ) ? raw : {};

	// Banners from before the template library kept Template 1's texts
	// in typography[*].text (per template since 2026-10-13).
	const legacyContent = isRecord( source.content )
		? ( source.content as UnknownRecord )[ 'template-1' ]
		: undefined;
	const legacyTypography = isRecord( legacyContent )
		? legacyContent.typography
		: source.typography;

	const rawContent =
		isRecord( source.content ) && isRecord( source.content.texts )
			? source.content
			: null;
	const content = rawContent
		? normalizeContent( rawContent, defaults.content )
		: legacyContentFromTypography( legacyTypography, defaults.content );

	const rawCountdown = isRecord( source.countdown ) ? source.countdown : {};
	const afterExpiry = toStr(
		rawCountdown.afterExpiry,
		defaults.countdown.afterExpiry
	);
	const countdown: CountdownSettings = {
		endDate: toStr( rawCountdown.endDate, defaults.countdown.endDate ),
		endTime: toStr( rawCountdown.endTime, defaults.countdown.endTime ),
		timezone:
			toStr( rawCountdown.timezone, defaults.countdown.timezone ) ||
			'site',
		afterExpiry: afterExpiry === 'show' ? 'show' : 'hide',
	};

	const priority = Number( source.priority );

	return {
		id: toStr( source.id, '' ),
		name: toStr( source.name, defaults.name ).trim() || defaults.name,
		// Banners saved before multi-banner support were always live.
		enabled: toBool( source.enabled, source.id === undefined ),
		priority: Number.isFinite( priority )
			? Math.max( 0, Math.min( 999, Math.round( priority ) ) )
			: defaults.priority,
		isDefault: toBool( source.isDefault, false ),
		templateId: toStr( source.templateId, '' ).trim() || DEFAULT_TEMPLATE,
		content,
		countdown,
		displayRules: normalizeDisplayRules( source.displayRules ),
		updatedAt: toStr( source.updatedAt, '' ) || undefined,
	};
};

const toStrList = ( value: unknown ): string[] =>
	Array.isArray( value )
		? value
				.filter(
					( item ) =>
						typeof item === 'string' || typeof item === 'number'
				)
				.map( ( item ) => String( item ).trim() )
				.filter( Boolean )
		: [];

const toIdList = ( value: unknown ): number[] =>
	Array.isArray( value )
		? value
				.map( ( item ) => Number( item ) )
				.filter( ( item ) => Number.isInteger( item ) && item > 0 )
		: [];

const toNumber = (
	value: unknown,
	fallback: number,
	min: number,
	max: number
): number => {
	const num = Number( value );
	if ( ! Number.isFinite( num ) ) {
		return fallback;
	}
	return Math.max( min, Math.min( max, Math.round( num ) ) );
};

const pick = < T extends string >(
	value: unknown,
	allowed: readonly T[],
	fallback: T
): T => ( allowed.includes( value as T ) ? ( value as T ) : fallback );

/**
 * Merge raw display rules with defaults and coerce types.
 *
 * @param raw Display rules as saved.
 */
export const normalizeDisplayRules = ( raw: unknown ): DisplayRules => {
	const d = JSON.parse(
		JSON.stringify( defaultDisplayRules )
	) as DisplayRules;
	const r = isRecord( raw ) ? raw : {};
	const visitors = isRecord( r.visitors ) ? r.visitors : {};
	const schedule = isRecord( r.schedule ) ? r.schedule : {};
	const placement = isRecord( r.placement ) ? r.placement : {};
	const trigger = isRecord( r.trigger ) ? r.trigger : {};
	const close = isRecord( r.closeBehavior ) ? r.closeBehavior : {};
	const isDateTime = ( value: string ) =>
		/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test( value );
	const start = toStr( schedule.start, '' );
	const end = toStr( schedule.end, '' );

	return {
		// Older banners had no switch: a hidden page meant "custom".
		showOnAllPages: toBool(
			r.showOnAllPages,
			toIdList( r.hiddenPageIds ).length === 0
		),
		hiddenPageIds: toIdList( r.hiddenPageIds ),
		visitors: {
			loginState: pick(
				visitors.loginState,
				[ 'all', 'logged_in', 'logged_out' ] as const,
				d.visitors.loginState
			),
			roles: toStrList( visitors.roles ),
			visitorType: pick(
				visitors.visitorType,
				[ 'all', 'new', 'returning' ] as const,
				d.visitors.visitorType
			),
		},
		devices: normalizeDevices( r.devices ),

		schedule: {
			enabled: toBool( schedule.enabled, false ),
			start: isDateTime( start ) ? start : '',
			end: isDateTime( end ) ? end : '',
			days: Array.isArray( schedule.days )
				? Array.from(
						new Set(
							schedule.days
								.map( ( day ) => Number( day ) )
								.filter(
									( day ) =>
										Number.isInteger( day ) &&
										day >= 0 &&
										day <= 6
								)
						)
				  )
				: d.schedule.days,
		},
		placement: {
			position: pick(
				placement.position,
				[ 'top', 'bottom' ] as const,
				d.placement.position
			),
			behavior: pick(
				placement.behavior,
				[ 'fixed', 'static' ] as const,
				d.placement.behavior
			),
			pushContent: toBool( placement.pushContent, true ),
			zIndex: toNumber(
				placement.zIndex,
				d.placement.zIndex,
				1,
				2147483647
			),
		},
		trigger: {
			type: pick(
				trigger.type,
				[ 'immediate', 'delay', 'scroll' ] as const,
				d.trigger.type
			),
			delaySeconds: toNumber(
				trigger.delaySeconds,
				d.trigger.delaySeconds,
				0,
				600
			),
			scrollPercent: toNumber(
				trigger.scrollPercent,
				d.trigger.scrollPercent,
				0,
				100
			),
		},
		closeBehavior: {
			// Older banners stored whole days.
			rememberHours:
				close.rememberHours !== undefined
					? toNumber( close.rememberHours, 0, 0, 8760 )
					: toNumber( close.rememberDays, 0, 0, 365 ) * 24,
		},
	};
};

/**
 * Convert a size value to CSS. Plain numbers become px, "Auto"/empty
 * becomes undefined (let the stylesheet decide).
 *
 * @param value Raw value from settings, e.g. "24", "24px", "Auto", "50%".
 */
export const formatUnit = ( value: unknown ): string | undefined => {
	if ( value === undefined || value === null ) {
		return undefined;
	}
	const str = String( value ).trim();
	if ( ! str || str.toLowerCase() === 'auto' ) {
		return undefined;
	}
	if ( /^-?\d+(\.\d+)?$/.test( str ) ) {
		return `${ str }px`;
	}
	return str;
};

/**
 * Line height: small plain numbers (<= 4) stay unitless multipliers
 * (e.g. 1.4), bigger plain numbers are treated as px.
 *
 * @param value Raw value from settings.
 */
export const formatLineHeight = ( value: unknown ): string | undefined => {
	const str = value === undefined || value === null ? '' : String( value );
	const trimmed = str.trim();
	if ( ! trimmed ) {
		return undefined;
	}
	if ( /^\d+(\.\d+)?$/.test( trimmed ) ) {
		return parseFloat( trimmed ) <= 4 ? trimmed : `${ trimmed }px`;
	}
	return trimmed;
};

/**
 * CSS font-family value. Names are quoted because names like
 * "Source Sans 3" are invalid in CSS without quotes.
 *
 * @param family Font family name from settings.
 */
export const formatFontFamily = ( family: string ): string | undefined => {
	const name = family.trim();
	if ( ! name ) {
		return undefined;
	}
	if ( /^(inherit|initial|serif|sans-serif|monospace)$/i.test( name ) ) {
		return name;
	}
	return `"${ name.replace( /["\\]/g, '' ) }", sans-serif`;
};

/**
 * Build a safe CSS url() value.
 *
 * @param url Image URL.
 */
export const cssUrl = ( url: string ): string =>
	`url("${ url.replace( /["\\\n\r]/g, ( char ) =>
		encodeURIComponent( char )
	) }")`;

export const isValidHexColor = ( value: string ): boolean =>
	/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test( value.trim() );

export const isValidHttpUrl = ( value: string ): boolean => {
	if ( ! value ) {
		return true;
	}
	if ( value.startsWith( '/' ) || value.startsWith( '#' ) ) {
		return true;
	}
	try {
		const parsed = new URL( value );
		return parsed.protocol === 'http:' || parsed.protocol === 'https:';
	} catch ( e ) {
		return false;
	}
};

/**
 * Read the site timezone that PHP localized onto a window object.
 *
 * @param source e.g. window.wplms_admin_support or window.wplms_admin_support_frontend.
 */
export const readSiteTimezone = ( source: unknown ): SiteTimezone => {
	const data =
		isRecord( source ) && isRecord( source.site_timezone )
			? source.site_timezone
			: {};
	const offset = Number( data.gmtOffset );
	return {
		timezone: toStr( data.timezone, '' ),
		gmtOffset: Number.isFinite( offset ) ? offset : 0,
	};
};

const isValidTimeZone = ( timeZone: string ): boolean => {
	try {
		new Intl.DateTimeFormat( 'en-US', { timeZone } );
		return true;
	} catch ( e ) {
		return false;
	}
};

/** Offset (ms) of an IANA timezone from UTC at a given instant. */
const getTimeZoneOffsetMs = ( utcMs: number, timeZone: string ): number => {
	const parts = new Intl.DateTimeFormat( 'en-US', {
		timeZone,
		hourCycle: 'h23',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	} ).formatToParts( new Date( utcMs ) );

	const get = ( type: string ): number =>
		Number( parts.find( ( part ) => part.type === type )?.value || 0 );

	const asUtc = Date.UTC(
		get( 'year' ),
		get( 'month' ) - 1,
		get( 'day' ),
		get( 'hour' ) % 24,
		get( 'minute' ),
		get( 'second' )
	);
	return asUtc - utcMs;
};

/**
 * Resolve the countdown end moment (epoch ms) honouring the timezone setting.
 * Returns null when no valid end date is configured.
 *
 * @param countdown    Countdown settings.
 * @param siteTimezone Site timezone localized by PHP.
 */
export const getCountdownTarget = (
	countdown: CountdownSettings,
	siteTimezone: SiteTimezone
): number | null => {
	const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec( countdown.endDate );
	if ( ! dateMatch ) {
		return null;
	}
	const timeMatch = /^(\d{1,2}):(\d{2})/.exec( countdown.endTime || '' );

	const year = Number( dateMatch[ 1 ] );
	const month = Number( dateMatch[ 2 ] ) - 1;
	const day = Number( dateMatch[ 3 ] );
	const hour = timeMatch ? Number( timeMatch[ 1 ] ) : 23;
	const minute = timeMatch ? Number( timeMatch[ 2 ] ) : 59;

	if ( countdown.timezone === 'local' ) {
		return new Date( year, month, day, hour, minute, 0 ).getTime();
	}

	const wallClockAsUtc = Date.UTC( year, month, day, hour, minute, 0 );
	const timeZone =
		countdown.timezone === 'site'
			? siteTimezone.timezone
			: countdown.timezone;

	if ( timeZone && isValidTimeZone( timeZone ) ) {
		// Two passes so the right offset is used around DST changes.
		const firstGuess =
			wallClockAsUtc - getTimeZoneOffsetMs( wallClockAsUtc, timeZone );
		return wallClockAsUtc - getTimeZoneOffsetMs( firstGuess, timeZone );
	}

	// Site uses a manual UTC offset (or an unknown zone): fall back to it.
	return wallClockAsUtc - siteTimezone.gmtOffset * 60 * 60 * 1000;
};

export interface TimeLeft {
	/** Whole days left, not padded (e.g. "5"). */
	days: string;
	/** Total hours left, padded (e.g. "140"). */
	hours: string;
	/** Hours of the current day, padded (e.g. "20"). */
	hoursOfDay: string;
	minutes: string;
	seconds: string;
	expired: boolean;
}

/**
 * @param target Countdown end (epoch ms) or null when not configured.
 * @param now    Current time (epoch ms).
 */
export const getTimeLeft = ( target: number | null, now: number ): TimeLeft => {
	const distance = target === null ? 0 : target - now;
	if ( distance <= 0 ) {
		return {
			days: '0',
			hours: '00',
			hoursOfDay: '00',
			minutes: '00',
			seconds: '00',
			expired: target !== null,
		};
	}
	const totalSeconds = Math.floor( distance / 1000 );
	const pad = ( n: number ) => String( n ).padStart( 2, '0' );
	return {
		days: String( Math.floor( totalSeconds / 86400 ) ),
		hours: pad( Math.floor( totalSeconds / 3600 ) ),
		hoursOfDay: pad( Math.floor( ( totalSeconds % 86400 ) / 3600 ) ),
		minutes: pad( Math.floor( ( totalSeconds % 3600 ) / 60 ) ),
		seconds: pad( totalSeconds % 60 ),
		expired: false,
	};
};

interface LoadFontsOptions {
	/** Document to inject into. */
	doc?: Document;
	/** Fonts the template always needs (see TemplateConfig.fonts). */
	extraFamilies?: string[];
	/** Use a different id per banner so banners do not replace each other's fonts. */
	linkId?: string;
}

/**
 * Inject (or update) a Google Fonts stylesheet for the fonts used by
 * the visible text elements. System fonts are skipped.
 *
 * @param typography Typography settings.
 * @param options    Options.
 */
export const loadBannerFonts = (
	typography: Record< string, TypographySettings >,
	options: LoadFontsOptions = {}
): void => {
	const {
		doc = document,
		extraFamilies = [],
		linkId = 'wplms-sticky-banner-fonts',
	} = options;

	const families = Array.from(
		new Set( [
			...Object.values( typography )
				.filter( ( item ) => item.isVisible )
				.map( ( item ) => item.fontFamily )
				.filter( ( family ) => GOOGLE_FONTS.includes( family ) ),
			...extraFamilies,
		] )
	).sort();

	const existing = doc.getElementById( linkId ) as HTMLLinkElement | null;

	if ( ! families.length ) {
		existing?.remove();
		return;
	}

	const query = families
		.map( ( family ) => {
			const weights =
				GOOGLE_FONT_WEIGHTS[ family ] ?? DEFAULT_GOOGLE_FONT_WEIGHTS;
			const name = family.replace( / /g, '+' );
			return weights
				? `family=${ name }:wght@${ weights }`
				: `family=${ name }`;
		} )
		.join( '&' );
	const href = `https://fonts.googleapis.com/css2?${ query }&display=swap`;

	if ( existing ) {
		if ( existing.href !== href ) {
			existing.href = href;
		}
		return;
	}

	const link = doc.createElement( 'link' );
	link.id = linkId;
	link.rel = 'stylesheet';
	link.href = href;
	doc.head.appendChild( link );
};

/**
 * Validate settings before saving. Returns human readable problems.
 *
 * @param settings Settings to validate.
 */
export const validateSettings = (
	settings: StickyBannerSettingsPayload
): string[] => {
	const errors: string[] = [];

	if ( ! isValidHttpUrl( settings.content.ctaLink ) ) {
		errors.push(
			'CTA button: link must start with http://, https:// or /.'
		);
	}

	if ( ! settings.name.trim() ) {
		errors.push( 'Please give the banner a name.' );
	}

	const rules = settings.displayRules;
	if ( ! DEVICE_KEYS.some( ( key ) => rules.devices[ key ] ) ) {
		errors.push( 'Display rules: select at least one device.' );
	}
	if ( rules.schedule.enabled ) {
		if ( ! rules.schedule.days.length ) {
			errors.push( 'Schedule: select at least one day of the week.' );
		}
		if (
			rules.schedule.start &&
			rules.schedule.end &&
			rules.schedule.end <= rules.schedule.start
		) {
			errors.push( 'Schedule: the end must be after the start.' );
		}
	}

	return errors;
};

/**
 * Validate a template before saving. Returns human readable problems.
 *
 * @param design Template to validate.
 */
export const validateTemplate = ( design: TemplateDesign ): string[] => {
	const errors: string[] = [];
	if ( ! design.name.trim() ) {
		errors.push( 'Please give the template a name.' );
	}
	COMPONENTS.forEach( ( definition ) => {
		const component = design.components[ definition.key ];
		definition.styleFields
			.filter(
				( field ) =>
					field.type === 'color' &&
					( ! field.variants ||
						field.variants.includes( component.variant ) )
			)
			.forEach( ( field ) => {
				if ( ! isValidHexColor( component.style[ field.key ] || '' ) ) {
					errors.push(
						`${ definition.label } – ${ field.label }: use a hex color like #2E4450.`
					);
				}
			} );
		definition.textFields.forEach( ( field ) => {
			if ( ! isValidHexColor( design.typography[ field.key ].color ) ) {
				errors.push(
					`${ field.label }: color must be a hex value like #2E4450.`
				);
			}
		} );
	} );
	return errors;
};
